import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import MakeBatchWizard from '../components/lab/MakeBatchWizard';
import { listBatches, listLabFormulas, listMethods } from '../lib/labData';
import { containerCount, formatBatchG, yieldPercent } from '../lib/lab';
import { downloadCsv } from '../lib/csv';
import './Lab.css';

const DATE = new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'short', year: 'numeric' });

const TABS = [
  { id: 'progress', label: 'In progress' },
  { id: 'log', label: 'Batch log' },
];

const LIVE = ['planned', 'in_progress', 'on_hold'];

const STATUS_LABEL = {
  planned: 'Planned',
  in_progress: 'In progress',
  completed: 'Completed',
  on_hold: 'On hold',
  abandoned: 'Abandoned',
};

// Production: making things, and the record of what was made. The Lab owns
// what a formula is; this owns what happened when you made it.
export default function Production() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(TABS[0].id);
  const [batches, setBatches] = useState([]);
  const [formulas, setFormulas] = useState([]);
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wizard, setWizard] = useState(false);

  const [formulaFilter, setFormulaFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');

  const load = useCallback(async () => {
    setError(null);
    try {
      const [b, f, m] = await Promise.all([listBatches(), listLabFormulas(), listMethods()]);
      setBatches(b);
      setFormulas(f);
      setMethods(m);
      // Land on whatever there is to do: if nothing is in progress, the log
      // is the useful tab.
      if (!b.some((x) => LIVE.includes(x.status))) setTab('log');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ?make=<formulaId> opens the wizard straight from a formula page.
  const requested = params.get('make');
  useEffect(() => {
    if (requested && formulas.length) setWizard(true);
  }, [requested, formulas]);

  const live = batches.filter((b) => LIVE.includes(b.status));
  const logged = useMemo(
    () =>
      batches
        .filter((b) => !LIVE.includes(b.status))
        .filter((b) => formulaFilter === 'All' || b.formula?.family_id === formulaFilter)
        .filter((b) => statusFilter === 'All' || b.status === statusFilter),
    [batches, formulaFilter, statusFilter]
  );

  const closeWizard = () => {
    setWizard(false);
    if (requested) {
      params.delete('make');
      setParams(params, { replace: true });
    }
  };

  const handleExport = () => {
    const rows = [
      ['Date made', 'Batch code', 'Formula', 'Version', 'Variant', 'Planned units', 'Actual units', 'Yield %', 'Batch size (g)', 'Expiry', 'Status', 'Notes'],
      ...logged.map((b) => {
        const planned = containerCount(b.containers);
        return [
          b.date_completed || b.made_on || '',
          b.batch_code || '',
          b.formula?.family?.name || '',
          b.formula?.version_number ? `v${b.formula.version_number}` : '',
          b.variant?.name || '',
          planned || '',
          b.actual_yield_units ?? '',
          yieldPercent(b.actual_yield_units, planned) ?? '',
          b.planned_batch_g ?? '',
          b.expiry_date || '',
          STATUS_LABEL[b.status] || b.status,
          b.notes || '',
        ];
      }),
    ];
    downloadCsv('batch-log.csv', rows);
  };

  return (
    <>
      <PageHeader
        title="Production"
        subtitle="Make a batch, follow the sheet, record what came out."
        actions={
          <button className="primary-action" onClick={() => setWizard(true)}>
            + Make a batch
          </button>
        }
      />

      <div className="view-tabs">
        {TABS.map(({ id, label }) => (
          <button
            key={id}
            className={'view-tab' + (tab === id ? ' active' : '')}
            onClick={() => setTab(id)}
          >
            {label}
            {id === 'progress' && live.length > 0 && <span className="tab-count">{live.length}</span>}
          </button>
        ))}
      </div>

      {error && <p className="formula-error">Couldn't load production: {error}</p>}
      {loading && <p className="formula-loading">Loading…</p>}

      {!loading && tab === 'progress' &&
        (live.length === 0 ? (
          <div className="lab-empty">
            <p>Nothing on the bench right now.</p>
            <button className="primary-action" onClick={() => setWizard(true)}>
              + Make a batch
            </button>
          </div>
        ) : (
          <ul className="lab-rows">
            {live.map((b) => {
              // Batches that predate the redesign have no code, no container
              // breakdown and no planned_batch_g — only the older
              // target_batch_g. Falling back keeps them legible instead of
              // rendering a blank row saying "0 planned".
              const size = b.planned_batch_g ?? b.target_batch_g;
              const plannedUnits = containerCount(b.containers) || b.expected_units || null;
              const label = `${b.formula?.family?.name || 'Batch'}${b.variant?.name ? ` — ${b.variant.name}` : ''}`;
              return (
                <li className="lab-row" key={b.id}>
                  <button className="lab-row-main" onClick={() => navigate(`/production/batches/${b.id}`)}>
                    <span className="lab-row-name">{b.batch_code || label}</span>
                    {b.batch_code && <span className="lab-row-variants">{label}</span>}
                    <span className={'lab-pill status-' + b.status}>{STATUS_LABEL[b.status]}</span>
                    {size != null && <span className="lab-row-meta">{formatBatchG(size)}</span>}
                    {plannedUnits && <span className="lab-row-meta">{plannedUnits} planned</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        ))}

      {!loading && tab === 'log' && (
        <>
          <div className="lab-toolbar">
            <select
              className="lab-select"
              value={formulaFilter}
              onChange={(e) => setFormulaFilter(e.target.value)}
            >
              <option value="All">All formulas</option>
              {formulas.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
            <select
              className="lab-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="All">All statuses</option>
              <option value="completed">Completed</option>
              <option value="abandoned">Abandoned</option>
            </select>
            <button className="ghost-button" onClick={handleExport} disabled={!logged.length}>
              Export CSV
            </button>
          </div>

          {logged.length === 0 ? (
            <div className="lab-empty">
              <p>
                No batches yet. When you make something, it'll show up here with what you actually
                got out of it.
              </p>
              <button className="primary-action" onClick={() => setWizard(true)}>
                + Make a batch
              </button>
            </div>
          ) : (
            <div className="table-scroll">
              <table className="lab-table batch-log">
                <thead>
                  <tr>
                    <th>Date made</th>
                    <th>Batch code</th>
                    <th>Formula</th>
                    <th>Variant</th>
                    <th className="numeric">Planned</th>
                    <th className="numeric">Actual</th>
                    <th className="numeric">Yield</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {logged.map((b) => {
                    const planned = containerCount(b.containers) || b.expected_units || 0;
                    const pct = yieldPercent(b.actual_yield_units, planned);
                    return (
                      <tr
                        key={b.id}
                        className="batch-log-row"
                        onClick={() => navigate(`/production/batches/${b.id}`)}
                      >
                        <td>
                          {b.date_completed || b.made_on
                            ? DATE.format(new Date(`${b.date_completed || b.made_on}T12:00:00`))
                            : '—'}
                        </td>
                        <td className="batch-log-code">{b.batch_code || '—'}</td>
                        <td>
                          {/* Links to the exact version used, not to
                              whatever the formula looks like now. */}
                          <Link
                            to={`/lab/formulas/${b.formula?.family_id}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {b.formula?.family?.name} v{b.formula?.version_number}
                          </Link>
                        </td>
                        <td>{b.variant?.name || '—'}</td>
                        <td className="numeric">{planned || '—'}</td>
                        <td className="numeric">{b.actual_yield_units ?? '—'}</td>
                        <td className="numeric">{pct == null ? '—' : `${pct}%`}</td>
                        <td className="batch-log-notes" title={b.notes || ''}>
                          {b.notes || ''}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {wizard && (
        <MakeBatchWizard
          formulas={formulas}
          methods={methods}
          initialFormulaId={requested || ''}
          onClose={closeWizard}
          onStarted={(batch) => {
            closeWizard();
            navigate(`/production/batches/${batch.id}`);
          }}
        />
      )}
    </>
  );
}
